import { i18n } from "./i18nService";
import { redirectTo, trackAndRedirectHandler } from "./navigationService";
import { trackEvent } from "./analyticsService";
import { getVoucherDurationText } from "./copyService";
import { historyMethods } from "../utils/historyMethods";
import { routes } from "../routes";
import { getAppState } from "../state";
import { getGlobalButtonInfo, getGlobalButtonFocusPath } from "../errors/globalButtons";
import { showAndReportUserRelatedError, getInternetConnectivityModalConfig } from "../errors/utils";
import { logout } from "./authenticationService";
import { displayCWPrices, displayEpisodePrices, expiredContent } from "../utils/tvodUtils";
import { buyAssetDetails } from "./mediaService";

const SIGNUP = "signup";
const LOGIN_SIGNUP = "login_signup";
const LOGIN = "login";
const RETRY = "retry";
const BACK = "back";
const CLOSE = "close";
const RENT = "rent";
const PURCHASE = "purchase";
const CANCEL = "cancel";
const DETAIL = "detail";
const CW = "cw";
const MY_LIST = "library";

/**
 * Shows a resolution modal in case the received condition is a truthy value, to let user
 * choose between 4K or HD content playback. Needs a React component class context since
 * this method is making use of this.props and other inner context properties (this.play() method).
 * @param {Boolean|Object} condition - Condition that determines whether resolution modal
 * should be shown or not.
 * @public
 */
export async function showResolutionModal(condition) {
  const {
    stateActions: { showGlobalModal },
    setFocus
  } = this.props;
  if (condition) {
    const selectQualityModalConfig = {
      modalText: i18n.get("global.4Ksupported"),
      buttons: [
        {
          label: i18n.get("global.play4K"),
          focusPath: "play4KModal_passThru",
          buttonHandler: () => this.play(true, true)
        },
        {
          label: i18n.get("global.playHD"),
          focusPath: "playHDModal_passThru",
          buttonHandler: () => this.play(false, true)
        }
      ]
    };
    showGlobalModal(selectQualityModalConfig);
    setFocus(selectQualityModalConfig.buttons[0].focusPath);
  } else {
    this.play(false);
  }
}

/**
 * Shows an 'exit signup' modal in case user press back button (either on remote or keyboard) during
 * the signup process to exit the flow. Needs a React component class context since
 * this method is making use of this.props and other inner context properties (this.closeModal() method).
 * @public
 */
export function showExitSignupModal() {
  const {
    stateActions: { showGlobalModal },
    setFocus
  } = this.props;
  const exitModalConfig = {
    modalText: `${i18n.get("global.modals.quit.paragraph1")}${i18n.get("global.modals.quit.paragraph2")}`,
    buttons: [
      {
        label: i18n.get("global.cancel"),
        focusPath: "noOkModal_passThru",
        buttonHandler: () => this.closeModal()
      },
      {
        label: i18n.get("global.yes"),
        focusPath: "okModal_passThru",
        buttonHandler: () => redirectTo.call(this, routes.home, null, historyMethods.REPLACE)
      }
    ],
    dismissHandler: () => this.closeModal()
  };
  showGlobalModal(exitModalConfig);
  setFocus(exitModalConfig.buttons[0].focusPath);
}

// TODO: Use this method across application to avoid generating multiple class methods that look exactly the same.
/**
 * Hides a modal and make focus on a provided path. Needs a React component class context since
 * this method is making use of this.props.
 * @param {String} defaultFocusPath - Path where to focus at when modal is closed.
 * @public
 */
export function closeModal(defaultFocusPath) {
  const {
    stateActions: { hideGlobalModal },
    setFocus
  } = this.props;
  hideGlobalModal();
  setFocus(defaultFocusPath);
}

/**
 * Gets the modal for when the user is not logged and tries to play some content. In case device is
 * eligible to have a voucher, an extra button will appear to make reference to signup process, as in the modal text.
 */
export function showNotLoggedModal(
  { nextPageReference = undefined, backPageReference = undefined },
  closeCallback = null
) {
  const buttons = [
    {
      ...getGlobalButtonInfo(LOGIN_SIGNUP),
      buttonHandler: redirectTo.bind(
        this,
        routes.pinSignup,
        {
          ...(nextPageReference && { nextPage: nextPageReference }),
          ...(backPageReference && { backDestination: backPageReference })
        },
        historyMethods.REPLACE
      )
    },
    {
      ...getGlobalButtonInfo(LOGIN),
      buttonHandler: redirectTo.bind(
        this,
        routes.pin,
        {
          ...(nextPageReference && { nextPage: nextPageReference }),
          ...(backPageReference && { backDestination: backPageReference })
        },
        historyMethods.REPLACE
      )
    },
    ...(!!getAppState().voucherData
      ? [
          {
            ...getGlobalButtonInfo(SIGNUP, getVoucherDurationText(getAppState().voucherData.duration.duration)),
            buttonHandler: () => {
              trackEvent(SIGNUP, "details_popup_user_selects_joinnow");
              redirectTo.call(this, routes.signup);
            }
          }
        ]
      : []),
    {
      ...getGlobalButtonInfo("cancel"),
      buttonHandler: closeCallback || this.closeModal
    }
  ];
  showAndReportUserRelatedError.call(
    this,
    {
      modalTitle: i18n.get("pages.detail.invalidAction.loginSignupHeading"),
      modalText: !!getAppState().voucherData
        ? `${i18n.get("pages.detail.invalidAction.joinToPlay.paragraph1")}${i18n.get(
            "pages.detail.invalidAction.joinToPlay.paragraph2"
          )}`
        : i18n.get("pages.detail.invalidAction.loginToPlay"),
      buttons
    },
    { event: "Playback", status: "info", errorName: "loginToPlayError" }
  );
  this.props.setFocus(getGlobalButtonFocusPath(LOGIN_SIGNUP));
}

/**
 * Gets the modal for when the user is logged but its account status is not active, so it won't allow to
 * play any content.
 */
export function showInvalidStatusModal(closeCallback = null) {
  let modalText = i18n.get("global.errors.account.invalidStatus");
  let focusPath = CLOSE;
  let buttons = [
    ...(!!getAppState().voucherData
      ? [
          {
            ...getGlobalButtonInfo(
              "startWatching",
              getVoucherDurationText(getAppState().voucherData.duration.duration)
            ),
            buttonHandler: () => {
              trackEvent(SIGNUP, "details_popup_user_selects_startnow");
              redirectTo.call(this, routes.redeem);
            }
          }
        ]
      : []),
    {
      ...getGlobalButtonInfo(CLOSE),
      buttonHandler: closeCallback || this.closeModal
    }
  ];
  if (!!getAppState().voucherData) {
    focusPath = "startWatching";
    modalText = `${i18n.get("global.errors.account.invalidStatusEVoucher.paragraph1")}${i18n.get(
      "global.errors.account.invalidStatusEVoucher.paragraph2"
    )}`;
  }

  showAndReportUserRelatedError.call(this, {
    modalText,
    buttons
  });
  this.props.setFocus(getGlobalButtonFocusPath(focusPath));
}

/**
 * Creates a global modal where the options to be shown are either to retry the request or going back in history
 * (or just this last one).
 */
export function showRetryOrBackModal(catchError) {
  let focusPath = RETRY;
  const retry = async () => {
    this.props.stateActions.hideGlobalModal();
    this.setState({
      titleData: await this.fetchData()
    });
  };
  const back = () => {
    this.props.stateActions.hideGlobalModal();
    this.onBack();
  };
  const backButton = {
    ...getGlobalButtonInfo(BACK),
    buttonHandler: back
  };

  this.props.resumeNavigation();

  // FIXME: Create an error code for no connection instead of comparing the string
  if (getAppState().isOnline && catchError && catchError.message.indexOf("timeout") > -1) {
    this.props.stateActions.showGlobalModal(
      getInternetConnectivityModalConfig.call(this, {
        retryImplementation: retry,
        backImplementation: back,
        needsDismissableProp: false,
        isTimeout: true
      })
    );
  } else if (
    (catchError && (catchError.status === 400 || catchError.status === 404) && catchError.error_code === "51001") ||
    this.state.unAuthorizedAccess
  ) {
    // FIXME: Handle this kind of errors from the SDK itself not from the client app
    focusPath = BACK;
    this.setState({ unAuthorizedAccess: true });
    this.props.stateActions.showGlobalModal({
      dismissable: false,
      modalText: i18n.get("pages.detail.errors.unauthorizedAccessToTitle"),
      buttons: [backButton]
    });
  } else if (!getAppState().isOnline && !catchError.status && !catchError.response) {
    this.props.stateActions.showGlobalModal(
      getInternetConnectivityModalConfig.call(this, {
        retryImplementation: retry,
        backImplementation: back
      })
    );
  } else {
    this.props.stateActions.showGlobalModal({
      dismissable: false,
      modalText: i18n.get("global.errors.genericError"),
      buttons: [
        {
          ...getGlobalButtonInfo(RETRY),
          buttonHandler: retry
        },
        backButton
      ]
    });
  }
  this.props.setFocus(getGlobalButtonFocusPath(focusPath));
}

/**
 * Generates a single button for the modal.
 * @param {String} identifier - Button identifier.
 * @param {Function} callback - Function to be called when pressing the button.
 */
export function showSingleModalButton(identifier, callback) {
  return [
    {
      ...getGlobalButtonInfo(identifier),
      buttonHandler: callback
    }
  ];
}

export function getUnmanageableModal() {
  const { setFocus } = this.props;
  let text = `pages.nonchangeable.unManagedModal.text`;
  setTimeout(() => {
    setFocus("okButtonModalFocusPath");
  }, 0);
  return {
    dismissable: false,
    modalText: i18n.get(text),
    buttons: [
      {
        label: i18n.get("pages.nonchangeable.unManagedModal.okButton"),
        displayBlock: true,
        focusPath: "okButtonModalFocusPath",
        buttonHandler: () => redirectTo.call(this, routes.redeem, null, historyMethods.REPLACE)
      }
    ]
  };
}

export async function showForceLogoutError() {
  const {
    stateActions: { showGlobalModal },
    setFocus
  } = this.props;
  if (!!getAppState().forceLogout) {
    if (!!getAppState().isOnline) {
      const buttons = [
        {
          ...getGlobalButtonInfo("accept"),
          buttonHandler: () => {
            this.props.stateActions.hideGlobalModal();
            getAppState().forceLogout = false;
            logout();
            this.props.stateActions.userLoggedOut();
            redirectTo.call(this, routes.pin);
          }
        }
      ];
      showGlobalModal({
        modalText: i18n.get("global.errors.forceLogout.title"),
        buttons
      });
      setFocus(getGlobalButtonFocusPath("accept"));
    }
  }
}

export function showExpiredContentModal(closeCallback, data) {
  const {
    applicationState: { userLocale },
    stateActions,
    setFocus
  } = this.props;
  const fromEpisode = data.rentCtaData || data.purchaseCtaData ? true : false;
  const fromCW = data.products ? true : false;

  const episodeRentLabel = data.rentCtaData && displayEpisodePrices(data).rentPrice;
  const episodePurchaseLabel = data.purchaseCtaData && displayEpisodePrices(data).purchasePrice;
  const bothEpisodePrices =
    (data.rentLabel && data.oldRentLabel) || (data.purchaseLabel && data.oldPurchaseLabel) ? true : false;

  const cwRentPrice = data.products && displayCWPrices(data).rentPrice;
  const cwPurchasePrice = data.products && displayCWPrices(data).purchasePrice;
  const cwRentData = data.products ? displayCWPrices(data).rentCtaData : null;
  const cwPurchaseData = data.products ? displayCWPrices(data).purchaseCtaData : null;
  const bothCWPrices =
    data.products && (displayCWPrices(data).isOldNewRent || displayCWPrices(data).isOldNewPurchase) ? true : false;

  const contentStatus = data.license && expiredContent(data.license.state) ? true : false;

  const buttons = [
    (episodeRentLabel || cwRentPrice) && {
      ...getGlobalButtonInfo(RENT, episodeRentLabel ? episodeRentLabel : cwRentPrice),
      buttonHandler: trackAndRedirectHandler.bind(
        this,
        data.isOnDetail && fromEpisode ? DETAIL : fromCW ? CW : data.isOnMyList && MY_LIST,
        contentStatus ? "rent_now_again" : "rent_now_cta",
        routes.buyasset,
        buyAssetDetails.call(this, "rent", fromEpisode ? data.rentCtaData : fromCW && cwRentData),
        "TVOD-Rent",
        cwRentPrice && data
      )
    },
    (episodePurchaseLabel || cwPurchasePrice) && {
      ...getGlobalButtonInfo(PURCHASE, episodePurchaseLabel ? episodePurchaseLabel : cwPurchasePrice),
      buttonHandler: trackAndRedirectHandler.bind(
        this,
        fromEpisode ? DETAIL : fromCW ? CW : data.isOnMyList && MY_LIST,
        "buy_now_cta",
        routes.buyasset,
        buyAssetDetails.call(this, "purchase", fromEpisode ? data.purchaseCtaData : fromCW && cwPurchaseData),
        "TVOD-Buy",
        cwPurchasePrice && data
      )
    },
    {
      ...getGlobalButtonInfo(CANCEL),
      buttonHandler: closeCallback || this.closeModal
    }
  ].filter(Boolean);
  stateActions.showGlobalModal({
    modalTitle:
      data.isOnMyList || fromCW
        ? i18n.get("global.modals.expiredContent.title")
        : fromEpisode && i18n.get("global.modals.episodeContent.title"),
    modalText:
      data.isOnMyList || fromCW
        ? i18n.get("global.modals.expiredContent.description")
        : fromEpisode && i18n.get("global.modals.episodeContent.description"),
    // isWidthIncrease: userLocale === "fr" ? (bothCWPrices ? true : bothEpisodePrices ? true : false) : false,
    buttons
  });
  setFocus(getGlobalButtonFocusPath(episodeRentLabel || cwRentPrice ? RENT : PURCHASE));
}
